import { useEffect, useCallback, useState, useRef } from 'react';
import type { PanInfo } from 'framer-motion';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const DRAG_CLOSE_THRESHOLD_PX = 100;
const DRAG_VELOCITY_THRESHOLD = 500;
const MAX_HEIGHT_PERCENTAGE = 0.95;

export interface BottomSheetProps {
  /** Whether the bottom sheet is visible */
  isOpen: boolean;
  /** Callback when the sheet is dismissed */
  onClose: () => void;
  /** Title displayed in the header */
  title: string;
  /** Content to display in the sheet */
  children: React.ReactNode;
  /** Optional additional styles for the content area */
  className?: string;
}

export function BottomSheet({
  isOpen,
  onClose,
  title,
  children,
  className = ''
}: BottomSheetProps) {
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [calculatedMaxHeight, setCalculatedMaxHeight] = useState<number | null>(null);

  // Calculate max height based on container or viewport
  useEffect(() => {
    if (!isOpen || !wrapperRef.current) {
      return;
    }

    const calculateHeight = () => {
      const element = wrapperRef.current;
      if (!element) return;

      // Check if inside a transformed container (Storybook mobile decorator)
      // by comparing offsetParent dimensions to viewport
      const offsetParent = element.offsetParent as HTMLElement | null;

      let containerHeight: number;
      if (offsetParent && offsetParent !== document.body) {
        // Inside a positioned container - use its height
        containerHeight = offsetParent.clientHeight;
      } else {
        // Use viewport height
        containerHeight = window.innerHeight;
      }

      setCalculatedMaxHeight(Math.floor(containerHeight * MAX_HEIGHT_PERCENTAGE));
    };

    calculateHeight();
    window.addEventListener('resize', calculateHeight);
    return () => {
      window.removeEventListener('resize', calculateHeight);
    };
  }, [isOpen]);
  // Handle escape key to close
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  const handleDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (info.offset.y > DRAG_CLOSE_THRESHOLD_PX || info.velocity.y > DRAG_VELOCITY_THRESHOLD) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            className="absolute inset-0 z-40 bg-black/60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Sheet Wrapper - handles positioning and margins */}
          <motion.div
            ref={wrapperRef}
            className="absolute bottom-0 left-0 right-0 z-50 px-[5px]"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.2 }}
            onDragEnd={handleDragEnd}
            role="dialog"
            aria-modal="true"
            aria-labelledby="bottom-sheet-title"
          >
            {/* Sheet Container */}
            <div
              className="flex flex-col"
              style={{
                maxHeight: calculatedMaxHeight ? `${String(calculatedMaxHeight)}px` : '85vh'
              }}
            >
              {/* Header Tab with Chevron - Click to close */}
              <button
                type="button"
                onClick={onClose}
                className="flex justify-center cursor-pointer bg-transparent border-none p-0"
                style={{ marginBottom: '-8px' }}
                aria-label={t('accessibility.closeBottomSheet')}
              >
                <svg
                  width="146"
                  height="36"
                  viewBox="0 0 146 36"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Trapezoid shape */}
                  <path d="M0 36L20 0H126L146 36H0Z" fill="#3B7CC8" />
                  {/* Inner darker area */}
                  <path d="M10 36L26 6H120L136 36H10Z" fill="#2B6AA8" />
                  {/* Chevron icon */}
                  <path
                    d="M63 12L73 22L83 12"
                    stroke="#1E4D7A"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {/* Title Bar */}
              <div
                className="relative flex items-center justify-center py-3"
                style={{
                  background: 'linear-gradient(90deg, #4488C6 0%, #3B7CC8 50%, #4488C6 100%)'
                }}
              >
                {/* Top border accent */}
                <div
                  className="absolute top-0 left-0 right-0 h-1"
                  style={{ background: '#5A9AD8' }}
                />
                <h2
                  id="bottom-sheet-title"
                  className="font-mind-boggle text-4xl text-white"
                  style={{
                    textShadow: '2px 2px 0px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  {title}
                </h2>
              </div>

              {/* Content Area */}
              <div
                className={`flex-1 min-h-0 overflow-y-auto p-4 ${className}`}
                style={{ background: '#2B7DD4' }}
              >
                {children}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default BottomSheet;
